import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';

import { BehaviorSubject, filter, map, Observable, of, Subscription, withLatestFrom } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { GlobalBookingTripDates, PriceCalendarCTAParams, PriceCalendarParams } from '@fcom/common';
import { LocalDate, unsubscribe } from '@fcom/core/utils';
import { ButtonMode, ButtonSize, ButtonTheme, DateRange } from '@fcom/ui-components';
import { GlobalBookingTravelClass, TripType } from '@fcom/core';
import { PaxAmount } from '@fcom/dapi';
import { LocationPair } from '@fcom/common/store';
import {
  PriceCalendarComponent,
  PriceCalendarType,
} from '@fcom/common/components/price-calendar/price-calendar.component';

import { BookingWidgetService } from '../../services/booking-widget.service';
import { SelectorType } from '../../interfaces';
@Component({
  selector: 'fin-travel-dates-selector',
  templateUrl: 'travel-dates-selector.component.html',
  styleUrls: ['./travel-dates-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TravelDatesSelectorComponent implements OnInit, OnDestroy {
  private bookingWidgetService = inject(BookingWidgetService);

  readonly ButtonMode = ButtonMode;
  readonly ButtonSize = ButtonSize;
  readonly ButtonTheme = ButtonTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly TripType = TripType;
  readonly PriceCalendarType = PriceCalendarType;

  @Input()
  priceCalendarType: PriceCalendarType = PriceCalendarType.PRICES;

  @Input()
  tripType$: Observable<TripType> = of(undefined);

  @Input()
  travelClass$: Observable<GlobalBookingTravelClass> = of(undefined);

  @Input()
  paxAmount$: Observable<PaxAmount> = of(undefined);

  @Input()
  locations$: Observable<LocationPair> = of(undefined);

  @Input()
  disabled = false;

  @Input()
  previousFlightDepartureDate$: Observable<LocalDate> = of(null);

  @Input()
  travelDates$: Observable<GlobalBookingTripDates>;

  @Input()
  isOneway$: Observable<boolean> = of(false);

  @Input()
  flexibleDates = false;

  @Input()
  showFlexibleDatesSelection = true;

  @Input()
  showTripPriceFrom = true;

  @Input()
  showPerAdultPrice = true;

  @Input()
  showSingular?: keyof GlobalBookingTripDates;

  @Input()
  showSeparator = true;

  @Input()
  showAddReturn = false;

  @Input()
  showSubtitle = false;

  @Input()
  enableNewSearchAutomatically = false;

  @Input()
  isSearchEnabled = false;

  @Output()
  setTravelDates = new EventEmitter<GlobalBookingTripDates>();

  @Output()
  setFlexibleDates = new EventEmitter<boolean>();

  @Output()
  addReturn = new EventEmitter<void>();

  @Output()
  startSearch = new EventEmitter<void>();

  @Output()
  cancelSelection = new EventEmitter<void>();

  @ViewChild(PriceCalendarComponent)
  private priceCalendar: PriceCalendarComponent;

  subscription = new Subscription();

  modalOpen$ = new BehaviorSubject<boolean>(false);
  disabledDateRanges$: Observable<DateRange[]>;

  priceCalendarParams$: BehaviorSubject<PriceCalendarParams> = new BehaviorSubject(null);
  showEnhancedCalendar$: Observable<boolean>;
  ngOnInit(): void {
    this.subscription.add(
      this.modalOpen$
        .pipe(
          filter(Boolean),
          withLatestFrom(
            this.tripType$,
            this.travelClass$,
            this.paxAmount$,
            this.locations$,
            this.travelDates$,
            this.isOneway$
          ),
          map(([, tripType, travelClass, paxAmount, { origin, destination }, travelDates, isOneway]) => {
            return {
              origin: origin?.locationCode,
              destination: destination?.locationCode,
              tripType: isOneway ? TripType.ONEWAY : tripType,
              travelClass,
              paxAmount,
              initialTravelDates: travelDates,
            };
          })
        )
        .subscribe((params) => this.priceCalendarParams$.next(params))
    );

    this.disabledDateRanges$ = this.previousFlightDepartureDate$.pipe(
      map((departureDate: LocalDate) => (departureDate ? [[LocalDate.now(), departureDate.minusDays(1)]] : []))
    );

    this.subscription.add(
      this.bookingWidgetService.modifyType$
        ?.pipe(
          filter(Boolean),
          filter((modifyType) => modifyType === SelectorType.TRAVEL_DATES)
        )
        .subscribe(() => {
          this.openModal();
        })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscription);
  }

  openModal(): void {
    this.modalOpen$.next(true);
  }

  closePriceCalendar(): void {
    if (this.enableNewSearchAutomatically) {
      this.cancelSelection.emit();
      this.priceCalendar.startSearchWithSelectedDates(true);
    }
    this.modalOpen$.next(false);
    this.bookingWidgetService.resetModifyType();
  }

  priceCalendarCTAClicked(event: PriceCalendarCTAParams): void {
    this.setFlexibleDates.emit(event.flexibleDates);
    this.closePriceCalendar();
  }

  addReturnClicked(): void {
    this.addReturn.emit();
  }

  travelDatesChanged(travelDates: GlobalBookingTripDates): void {
    this.setTravelDates.emit(travelDates);
  }
}
